import React, { useEffect, useState, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { AlertTriangle, Activity, Map, Radio, ShieldAlert, Clock, Crosshair, BarChart2, Database, Grid } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker } from 'react-leaflet';
import { BINDA_LIST, statusColor, NASIONAL } from '../data/binda';
import { useStore, isOfficialProduct } from '../data/store';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { CheckCircle2, ShieldCheck, WifiOff, Filter } from 'lucide-react';
import { FilterBar, FilterField } from '../components/FilterBar';
const TICKER_SEP = ['/', '/', '/'].join('');
const tickerMessages = ['[14:32] LAPORAN KRITIS DITERIMA DARI BINDA PAPUA - ESKALASI KE MARKAS BESAR', '[14:15] TINGKAT PERINGATAN DINAIKKAN UNTUK DKI JAKARTA KARENA KUMPULAN MASSA', '[13:45] Pemeliharaan sistem dijadwalkan pada 02:00 WIB.', '[13:00] Skor KPI Nasional diperbarui: 89 (Kategori A).', '[12:30] ANOMALI CUACA TERDETEKSI DI SEKTOR 4'];
const incidentFeed = [{
  time: '14:32:05',
  type: 'KRITIS',
  desc: 'Aktivitas tidak dikenal dilaporkan di Sektor 7',
  loc: 'PAPUA'
}, {
  time: '14:15:22',
  type: 'WASPADA',
  desc: 'Kumpulan massa terdeteksi via SIGINT',
  loc: 'DKI JAKARTA'
}, {
  time: '13:45:10',
  type: 'INFO',
  desc: 'Patroli rutin selesai',
  loc: 'JAWA BARAT'
}, {
  time: '13:10:00',
  type: 'INFO',
  desc: 'Pergantian shift personel dicatat',
  loc: 'BALI'
}, {
  time: '12:55:30',
  type: 'PERHATIAN',
  desc: 'Latensi komunikasi terdeteksi',
  loc: 'MALUKU'
}, {
  time: '12:30:00',
  type: 'INFO',
  desc: 'Pembaruan data sensor cuaca',
  loc: 'NASIONAL'
}, {
  time: '12:15:45',
  type: 'WASPADA',
  desc: 'Pergerakan kapal mencurigakan',
  loc: 'KEPULAUAN RIAU'
}];
export default function CommandCenter() {
  const navigate = useNavigate();
  const alertsList = useStore(s => s.alerts);
  const kpiDataList = useStore(s => s.kpi);
  const reportsList = useStore(s => s.reports);
  const [time, setTime] = useState(new Date());
  const [showEmptyAlerts, setShowEmptyAlerts] = useState(false);
  const [showEmptyIncidents, setShowEmptyIncidents] = useState(false);
  const [simulateConnectionError, setSimulateConnectionError] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filterFields: FilterField[] = [{
    key: 'mode',
    label: 'Mode Tampilan',
    type: 'select',
    options: [{
      label: 'Nasional',
      value: 'Nasional'
    }, {
      label: 'Provinsi',
      value: 'Provinsi'
    }]
  }, {
    key: 'layer',
    label: 'Layer Peta',
    type: 'select',
    options: [{
      label: 'Semua Layer',
      value: 'Semua'
    }, {
      label: 'Heatmap',
      value: 'Heatmap'
    }, {
      label: 'Alert',
      value: 'Alert'
    }, {
      label: 'Laporan',
      value: 'Laporan'
    }, {
      label: 'Personel',
      value: 'Personel'
    }]
  }, {
    key: 'threat',
    label: 'Level Ancaman',
    type: 'urgencyBadge',
    options: [{
      label: 'Kritis',
      value: 'KRITIS'
    }, {
      label: 'Waspada',
      value: 'WASPADA'
    }, {
      label: 'Perhatian',
      value: 'PERHATIAN'
    }]
  }, {
    key: 'date',
    label: 'Periode Waktu',
    type: 'datePreset'
  }];
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="h-screen w-screen bg-[#0a0c10] text-foreground flex flex-col p-2 gap-2 font-sans overflow-hidden relative" data-id="element-865">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-866"></div>

      {/* Top Header - Ultra Slim */}
      <header className="flex justify-between items-center glass-panel-heavy border-b border-border/30 px-4 py-2 rounded shrink-0 z-10" data-id="element-867">
        <div className="flex items-center gap-4" data-id="element-868">
          <div className="w-9 h-9 flex items-center justify-center shrink-0" data-id="element-869">
            <img src="https://cdn.magicpatterns.com/uploads/hos8i69ZV8fMbtkJgKVvED/Logo_BIN_NEW.png" alt="Logo Badan Intelijen Negara Republik Indonesia" className="w-full h-full object-contain drop-shadow-[0_0_12px_rgba(var(--primary),0.4)]" data-id="element-870" />
          </div>
          <div data-id="element-871">
            <h1 className="text-lg font-heading font-bold tracking-widest uppercase text-primary text-glow-primary leading-tight" data-id="element-872">
              DENS CAKRA COMMAND CENTER
            </h1>
            <p className="text-[9px] text-muted-foreground font-mono tracking-widest uppercase leading-tight" data-id="element-873">
              National Monitoring &amp; Situational Awareness Center
            </p>
            <p className="text-[9px] italic text-primary/70 font-mono tracking-wider leading-tight" data-id="element-874">
              Velox Et Exactus
            </p>
          </div>
        </div>
        <div className="flex items-center gap-8" data-id="element-875">
          <div className="flex items-center gap-3" data-id="element-876">
            <div className="flex flex-col items-end" data-id="element-877">
              <div className="text-2xl font-mono font-bold tracking-wider text-[var(--status-active)] text-glow-active leading-none" data-id="element-878">
                {time.toLocaleTimeString('id-ID', {
                hour12: false
              })}
              </div>
              <div className="text-[9px] text-muted-foreground font-mono uppercase tracking-widest mt-1" data-id="element-879">
                {time.toLocaleDateString('id-ID', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
              </div>
            </div>
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-1.5 border rounded text-[10px] font-mono font-bold uppercase tracking-widest transition-colors ${showFilters ? 'bg-primary text-primary-foreground border-primary' : 'bg-background/50 border-border/50 text-muted-foreground hover:border-primary/50'}`} data-id="element-880">
            <Filter className="w-3 h-3 inline mr-1.5" data-id="element-881" />
            Filter
          </button>
          <button onClick={() => setSimulateConnectionError(!simulateConnectionError)} className="px-3 py-1.5 bg-[var(--status-standby)]/20 border border-[var(--status-standby)]/50 text-[var(--status-standby)] rounded text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-[var(--status-standby)]/40 transition-colors shadow-[0_0_10px_rgba(var(--status-standby),0.2)]" data-id="element-882">
            {simulateConnectionError ? 'Pulihkan Koneksi' : 'Simulasi Galat'}
          </button>
          <Link to="/" className="px-3 py-1.5 bg-destructive/20 border border-destructive/50 text-destructive rounded text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-destructive/40 transition-colors shadow-[0_0_10px_rgba(var(--destructive),0.2)]" data-id="element-883">
            Keluar Videowall
          </Link>
        </div>
      </header>

      {showFilters && <div className="px-4 z-10 shrink-0" data-id="element-884">
          <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} data-id="element-885" />
        </div>}

      {/* Main Grid */}
      {simulateConnectionError ? <div className="flex-1 flex items-center justify-center z-10" data-id="element-886">
          <ErrorState icon={WifiOff} title="KONEKSI TERPUTUS" description="Sambungan ke peladen pusat terputus. Pastikan perangkat Anda terhubung ke jaringan aman." actionLabel="Coba Ulang" onAction={() => setSimulateConnectionError(false)} variant="warning" className="w-full max-w-2xl bg-black/60 backdrop-blur-md border-border/30" data-id="element-887" />
        </div> : <div className="flex-1 grid grid-cols-12 gap-2 z-10 min-h-0" data-id="element-888">
          {/* Left Column: National Stats & Alerts */}
          <div className="col-span-3 flex flex-col gap-2 min-h-0" data-id="element-889">
            {/* National Statistics */}
            <Card className="glass-panel border-border/30 shrink-0" data-id="element-890">
              <CardHeader className="p-2 border-b border-border/30 bg-black/20" data-id="element-891">
                <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-892">
                  <Database className="w-3 h-3 text-primary" data-id="element-893" /> STATISTIK
                  NASIONAL
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 grid grid-cols-2 gap-2" data-id="element-894">
                <div className="bg-black/30 border border-border/20 rounded p-2 text-center" data-id="element-895">
                  <p className="text-2xl font-mono font-bold text-primary text-glow-primary leading-none" data-id="element-896">
                    {NASIONAL.totalPersonelAktif.toLocaleString('id-ID')}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground uppercase tracking-widest mt-1" data-id="element-897">
                    Personel Aktif
                  </p>
                </div>
                <div className="bg-black/30 border border-border/20 rounded p-2 text-center" data-id="element-898">
                  <p className="text-2xl font-mono font-bold text-[var(--status-active)] text-glow-active leading-none" data-id="element-899">
                    {NASIONAL.laporanHariIni.toLocaleString('id-ID')}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground uppercase tracking-widest mt-1" data-id="element-900">
                    Laporan Hari Ini
                  </p>
                </div>
                <div className="bg-black/30 border border-border/20 rounded p-2 text-center" data-id="element-901">
                  <p className="text-xl font-mono font-bold text-[var(--status-standby)] text-glow-standby leading-none" data-id="element-902">
                    {NASIONAL.laporanPrioritas}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground uppercase tracking-widest mt-1" data-id="element-903">
                    Prioritas
                  </p>
                </div>
                <div className="bg-black/30 border border-border/20 rounded p-2 text-center" data-id="element-904">
                  <p className="text-xl font-mono font-bold text-destructive text-glow-destructive leading-none" data-id="element-905">
                    {NASIONAL.laporanDarurat}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground uppercase tracking-widest mt-1" data-id="element-906">
                    Darurat
                  </p>
                </div>
                <div className="col-span-2 flex justify-between items-center bg-black/30 border border-border/20 rounded p-2" data-id="element-907">
                  <div className="text-center flex-1 border-r border-border/20" data-id="element-908">
                    <p className="text-sm font-mono font-bold text-destructive" data-id="element-909">
                      {NASIONAL.wilayahKritis}
                    </p>
                    <p className="text-[8px] font-mono text-muted-foreground" data-id="element-910">
                      KRITIS
                    </p>
                  </div>
                  <div className="text-center flex-1 border-r border-border/20" data-id="element-911">
                    <p className="text-sm font-mono font-bold text-[var(--status-standby)]" data-id="element-912">
                      {NASIONAL.wilayahWaspada}
                    </p>
                    <p className="text-[8px] font-mono text-muted-foreground" data-id="element-913">
                      WASPADA
                    </p>
                  </div>
                  <div className="text-center flex-1 border-r border-border/20" data-id="element-914">
                    <p className="text-sm font-mono font-bold text-[var(--status-info)]" data-id="element-915">
                      {NASIONAL.wilayahPerhatian}
                    </p>
                    <p className="text-[8px] font-mono text-muted-foreground" data-id="element-916">
                      PERHATIAN
                    </p>
                  </div>
                  <div className="text-center flex-1" data-id="element-917">
                    <p className="text-sm font-mono font-bold text-[var(--status-active)]" data-id="element-918">
                      {NASIONAL.wilayahNormal}
                    </p>
                    <p className="text-[8px] font-mono text-muted-foreground" data-id="element-919">
                      NORMAL
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alerts Panel */}
            <Card className="flex-1 glass-panel border-border/30 flex flex-col overflow-hidden" data-id="element-920">
              <CardHeader className="p-2 border-b border-border/30 bg-black/20 shrink-0 flex justify-between items-center" data-id="element-921">
                <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-922">
                  <AlertTriangle className="w-3 h-3 text-destructive" data-id="element-923" />{' '}
                  PERINGATAN AKTIF
                </CardTitle>
                <button onClick={() => setShowEmptyAlerts(!showEmptyAlerts)} className="text-[8px] font-mono text-muted-foreground hover:text-primary transition-colors uppercase" data-id="element-924">
                  {showEmptyAlerts ? 'Pulihkan' : 'Kosongkan'}
                </button>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto custom-scrollbar flex-1" data-id="element-925">
                {showEmptyAlerts ? <EmptyState icon={ShieldCheck} title="TIDAK ADA PERINGATAN" description="Seluruh sektor terpantau aman. Tidak ada peringatan aktif yang memerlukan eskalasi saat ini." className="h-full border-0 rounded-none" data-id="element-926" /> : <div className="divide-y divide-border/20" data-id="element-927">
                    {alertsList.map(alert => <div key={alert.id} className="p-2.5 hover:bg-white/5 transition-colors" data-id="element-928">
                        <div className="flex justify-between items-start mb-1" data-id="element-929">
                          <UrgencyBadge urgency={alert.type} className="text-[8px]" data-id="element-930" />
                          <span className="text-[9px] text-muted-foreground font-mono" data-id="element-931">
                            {alert.time}
                          </span>
                        </div>
                        <p className="text-[10px] font-mono text-foreground/90 leading-snug mb-1" data-id="element-932">
                          {alert.desc}
                        </p>
                        <div className="flex justify-between items-center text-[8px] font-mono text-muted-foreground uppercase" data-id="element-933">
                          <span data-id="element-934">{alert.source}</span>
                          <span className="text-primary" data-id="element-935">{alert.location}</span>
                        </div>
                      </div>)}
                  </div>}
              </CardContent>
            </Card>
          </div>

          {/* Center Column: Map & Heatmap */}
          <div className="col-span-6 flex flex-col gap-2 min-h-0" data-id="element-936">
            {/* Massive Map */}
            <Card className="flex-1 glass-panel border-border/30 relative overflow-hidden p-0" data-id="element-937">
              <div className="absolute inset-0 z-0 bg-[#0a0e17]" data-id="element-938">
                <MapContainer center={[-2.5489, 118.0149]} zoom={5} style={{
              height: '100%',
              width: '100%',
              background: 'transparent'
            }} zoomControl={false} attributionControl={false} data-id="element-939">
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png" data-id="element-940" />
                  {BINDA_LIST.map((region, idx) => {
                const color = statusColor(region.status);
                return <Fragment key={idx} data-id="element-941">
                        <CircleMarker center={[region.lat, region.lng]} radius={region.status === 'Kritis' ? 12 : region.status === 'Waspada' ? 8 : 4} className={region.status === 'Kritis' ? 'animate-pulse' : ''} pathOptions={{
                    color: color,
                    fillColor: color,
                    fillOpacity: region.status === 'Kritis' ? 0.8 : 0.5,
                    weight: region.status === 'Kritis' ? 2 : 1
                  }} data-id="element-942" />
                        {region.status === 'Kritis' && <CircleMarker center={[region.lat, region.lng]} radius={40} pathOptions={{
                    color: color,
                    fill: false,
                    weight: 1,
                    dashArray: '2 6'
                  }} data-id="element-943" />}
                      </Fragment>;
              })}
                </MapContainer>
              </div>
              {/* Map Overlay UI */}
              <div className="absolute top-2 left-2 z-10 flex gap-2" data-id="element-944">
                <div className="glass-panel-heavy px-2 py-1 rounded border border-primary/30 flex items-center gap-2" data-id="element-945">
                  <Crosshair className="w-3 h-3 text-primary animate-[spin_4s_linear_infinite]" data-id="element-946" />
                  <span className="text-[9px] font-mono text-primary tracking-widest" data-id="element-947">
                    SAT-COM TERHUBUNG
                  </span>
                </div>
              </div>
              <div className="absolute bottom-2 right-2 z-10 glass-panel-heavy p-1.5 rounded border border-border/30 text-right" data-id="element-948">
                <div className="text-[8px] font-mono text-muted-foreground" data-id="element-949">
                  KOORD: 118.0149° E, 2.5489° S
                </div>
                <div className="text-[8px] font-mono text-muted-foreground" data-id="element-950">
                  ELEV: 35,000 FT (SIM)
                </div>
              </div>
            </Card>

            {/* Regional Heatmap */}
            <Card className="h-32 glass-panel border-border/30 shrink-0 flex flex-col overflow-hidden" data-id="element-951">
              <CardHeader className="p-2 border-b border-border/30 bg-black/20 shrink-0" data-id="element-952">
                <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-953">
                  <Grid className="w-3 h-3 text-primary" data-id="element-954" /> HEATMAP AKTIVITAS
                  REGIONAL
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 flex-1 overflow-y-auto custom-scrollbar" data-id="element-955">
                <div className="grid grid-cols-10 gap-1" data-id="element-956">
                  {BINDA_LIST.map(region => {
                const color = statusColor(region.status);
                // Calculate opacity based on laporanHariIni (max ~100)
                const opacity = Math.max(0.2, Math.min(1, region.laporanHariIni / 100));
                return <div key={region.kode} className="relative group aspect-square rounded-sm border border-border/10 flex items-center justify-center overflow-hidden" style={{
                  backgroundColor: `color-mix(in srgb, ${color} ${opacity * 100}%, transparent)`
                }} data-id="element-957">
                        <span className="text-[8px] font-mono font-bold text-white/80 z-10 mix-blend-overlay" data-id="element-958">
                          {region.kode.replace('BINDA-', '')}
                        </span>
                        {/* Tooltip on hover */}
                        <div className="absolute inset-0 bg-black/90 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex flex-col items-center justify-center p-1 text-center" data-id="element-959">
                          <span className="text-[8px] font-mono font-bold text-white leading-tight" data-id="element-960">
                            {region.provinsi}
                          </span>
                          <span className="text-[7px] font-mono text-muted-foreground leading-tight" data-id="element-961">
                            {region.laporanHariIni} Lap
                          </span>
                        </div>
                      </div>;
              })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: KPI & Incident Feed */}
          <div className="col-span-3 flex flex-col gap-2 min-h-0" data-id="element-962">
            {/* KPI Panel */}
            <Card className="glass-panel border-border/30 shrink-0" data-id="element-963">
              <CardHeader className="p-2 border-b border-border/30 bg-black/20" data-id="element-964">
                <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-965">
                  <BarChart2 className="w-3 h-3 text-primary" data-id="element-966" /> INDEKS KINERJA
                  (KPI)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2" data-id="element-967">
                <div className="flex items-center justify-between mb-3 bg-black/30 p-2 rounded border border-border/20" data-id="element-968">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-969">
                    KPI Nasional
                  </span>
                  <span className="text-xl font-mono font-bold text-[var(--status-active)] text-glow-active leading-none" data-id="element-970">
                    {NASIONAL.kpiNasional}%
                  </span>
                </div>
                <div className="space-y-1.5" data-id="element-971">
                  {kpiDataList.slice(0, 4).map((kpi, idx) => <div key={kpi.kode} className="flex items-center justify-between text-[9px] font-mono" data-id="element-972">
                      <div className="flex items-center gap-2" data-id="element-973">
                        <span className="text-muted-foreground w-3" data-id="element-974">
                          {idx + 1}.
                        </span>
                        <span className="text-foreground/90" data-id="element-975">{kpi.name}</span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-976">
                        <span className="text-muted-foreground" data-id="element-977">
                          {kpi.responseTime}
                        </span>
                        <span className="text-primary font-bold w-6 text-right" data-id="element-978">
                          {kpi.score}
                        </span>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {/* Incident Feed */}
            <Card className="flex-1 glass-panel border-border/30 flex flex-col overflow-hidden" data-id="element-979">
              <CardHeader className="p-2 border-b border-border/30 bg-black/20 shrink-0 flex justify-between items-center" data-id="element-980">
                <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-981">
                  <Radio className="w-3 h-3 text-primary" data-id="element-982" /> FEED INSIDEN
                  LANGSUNG
                </CardTitle>
                <button onClick={() => setShowEmptyIncidents(!showEmptyIncidents)} className="text-[8px] font-mono text-muted-foreground hover:text-primary transition-colors uppercase" data-id="element-983">
                  {showEmptyIncidents ? 'Pulihkan' : 'Kosongkan'}
                </button>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto custom-scrollbar flex-1" data-id="element-984">
                {showEmptyIncidents ? <EmptyState icon={CheckCircle2} title="TIDAK ADA INSIDEN" description="Umpan insiden langsung kosong. Tidak ada aktivitas taktis yang dilaporkan dalam 24 jam terakhir." className="h-full border-0 rounded-none" data-id="element-985" /> : <div className="divide-y divide-border/20" data-id="element-986">
                    {reportsList.filter(isOfficialProduct).map(r => <div key={r.id} className="p-2.5 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate(`/reports/${r.id}`)} data-id="element-987">
                        <div className="flex justify-between items-start mb-1" data-id="element-988">
                          <UrgencyBadge urgency={r.urgency} className="text-[8px]" data-id="element-989" />
                          <span className="text-[8px] text-muted-foreground font-mono" data-id="element-990">
                            {r.time.split(' ')[1]}
                          </span>
                        </div>
                        <p className="text-[10px] font-mono text-foreground/90 leading-snug mb-1" data-id="element-991">
                          [ESKALASI] {r.title}
                        </p>
                        <p className="text-[8px] text-muted-foreground font-mono flex items-center gap-1" data-id="element-992">
                          <Map className="w-2 h-2" data-id="element-993" /> {r.region}
                        </p>
                      </div>)}
                    {incidentFeed.map((inc, i) => <div key={i} className="p-2.5 hover:bg-white/5 transition-colors" data-id="element-994">
                        <div className="flex justify-between items-start mb-1" data-id="element-995">
                          <UrgencyBadge urgency={inc.type} className="text-[8px]" data-id="element-996" />
                          <span className="text-[8px] text-muted-foreground font-mono" data-id="element-997">
                            {inc.time}
                          </span>
                        </div>
                        <p className="text-[10px] font-mono text-foreground/90 leading-snug mb-1" data-id="element-998">
                          {inc.desc}
                        </p>
                        <p className="text-[8px] text-muted-foreground font-mono flex items-center gap-1" data-id="element-999">
                          <Map className="w-2 h-2" data-id="element-1000" /> {inc.loc}
                        </p>
                      </div>)}
                  </div>}
              </CardContent>
            </Card>
          </div>
        </div>}

      {/* Bottom Ticker */}
      <div className="h-6 glass-panel-heavy border border-border/30 rounded flex items-center px-3 overflow-hidden shrink-0 z-10" data-id="element-1001">
        <div className="flex items-center gap-2 text-primary font-mono font-bold uppercase tracking-widest text-[9px] mr-4 shrink-0 border-r border-border/50 pr-4" data-id="element-1002">
          <Radio className="w-3 h-3 animate-pulse" data-id="element-1003" /> TICKER INTELIJEN
        </div>

        <div className="flex-1 overflow-hidden relative" data-id="element-1004">
          <div className="whitespace-nowrap animate-[marquee_40s_linear_infinite] text-[10px] font-mono text-muted-foreground" data-id="element-1005">
            {tickerMessages.map((msg, i) => <span key={i} data-id="element-1006">
                <span className="mx-4 text-primary" data-id="element-1007">{TICKER_SEP}</span>
                <span data-id="element-1008">{msg}</span>
              </span>)}
            <span className="mx-4 text-primary" data-id="element-1009">{TICKER_SEP}</span>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
      `
    }} data-id="element-1010" />
    </div>;
}